import { useState, useEffect } from "react";
import {
  Radar, Search, Loader2, Sparkles, ArrowRight, AlertTriangle, Bot, Braces,
  Wand2, CheckCircle2, XCircle, Globe, Plus, Trash2, RefreshCw, FileText, Tags,
  Rocket, Download, ExternalLink, Zap, ListChecks,
  Swords, Languages, Split, Link2, Trophy,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ManagedPages from "@/components/seo/ManagedPages";
import { useAuth } from "@/context/AuthContext";
import {
  seoAnalyzePage, seoFindBestPage, seoRewrite, seoSchema, scoreTone,
  seoListSites, seoCreateSite, seoDeleteSite, seoCrawlSite, seoSiteDashboard, seoSitePages,
  seoPublish, seoBulkOptimize, seoJobStatus, seoJobsList, seoPublishDraft,
  seoRunCompetitors, seoGetCompetitors, seoHreflang, seoCannibalization,
} from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const CLASS_STYLE = {
  EXCELLENT: "bg-emerald-100 text-emerald-700 border-emerald-200",
  GOOD: "bg-blue-100 text-blue-700 border-blue-200",
  "NEEDS IMPROVEMENT": "bg-amber-100 text-amber-700 border-amber-200",
  THIN: "bg-orange-100 text-orange-700 border-orange-200",
  OUTDATED: "bg-rose-100 text-rose-700 border-rose-200",
  BLOCKED: "bg-slate-200 text-slate-600 border-slate-300",
};
const PRIO_STYLE = {
  critical: "bg-rose-50 text-rose-700 border-rose-200",
  high: "bg-amber-50 text-amber-700 border-amber-200",
  recommended: "bg-blue-50 text-blue-700 border-blue-200",
  optional: "bg-slate-50 text-slate-600 border-slate-200",
};

const ScoreTile = ({ label, value, big }) => {
  const t = scoreTone(value);
  return (
    <div className={`rounded-xl border border-slate-200 bg-white p-4 ${big ? "col-span-2" : ""}`} data-testid={`score-tile-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</div>
      <div className="mt-1 flex items-end gap-2">
        <span className="font-heading text-3xl font-extrabold" style={{ color: t.color }}>{value}</span>
        <span className="mb-1 text-xs font-medium text-slate-400">/100</span>
      </div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${value}%`, backgroundColor: t.color }} />
      </div>
    </div>
  );
};

export default function SeoIntelligence() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [mode, setMode] = useState("page"); // 'page' | 'site'
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");

  // rewrite
  const [rewriting, setRewriting] = useState(false);
  const [rewrite, setRewrite] = useState(null);
  // schema
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [schema, setSchema] = useState(null);

  const runAnalyze = async (targetUrl) => {
    const u = (targetUrl || url).trim();
    if (!u) return;
    setLoading(true); setError(""); setReport(null); setRewrite(null); setSchema(null);
    try {
      if (mode === "site") {
        const best = await seoFindBestPage(u);
        toast.success(`Found your most content-rich page (${best.word_count} words)`);
        setUrl(best.best_url);
        const rep = await seoAnalyzePage(best.best_url);
        setReport(rep);
      } else {
        const rep = await seoAnalyzePage(u);
        setReport(rep);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not analyze this URL. Please check it and try again.");
    } finally {
      setLoading(false);
    }
  };

  const runRewrite = async () => {
    if (!report) return;
    setRewriting(true); setRewrite(null);
    try {
      const res = await seoRewrite(report.final_url, report.recommendations);
      setRewrite(res);
      toast.success("AI rewrite ready — review the optimized version below");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rewrite failed. Please try again.");
    } finally {
      setRewriting(false);
    }
  };

  const runSchema = async () => {
    if (!report) return;
    setSchemaLoading(true);
    try {
      const res = await seoSchema(report.final_url);
      setSchema(res.schema);
    } catch (e) {
      toast.error("Could not generate schema. Please try again.");
    } finally {
      setSchemaLoading(false);
    }
  };

  const cleanHtml = (h) => (h || "").replace(/<script[\s\S]*?<\/script>/gi, "");

  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(null);

  const runPublish = async () => {
    if (!rewrite || !report) return;
    setPublishing(true);
    try {
      const res = await seoPublish({
        url: report.final_url,
        new_title: rewrite.new_title,
        meta_description: rewrite.meta_description,
        article_html: rewrite.article_html,
        changes: rewrite.changes,
      });
      setPublished(res);
      toast.success(`Published live at ${res.live_url} (v${res.version_no})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  const exportHtml = () => {
    if (!rewrite) return;
    const doc = `<!doctype html><html><head><meta charset="utf-8"><title>${rewrite.new_title}</title>` +
      `<meta name="description" content="${(rewrite.meta_description || "").replace(/"/g, "&quot;")}"></head>` +
      `<body>${cleanHtml(rewrite.article_html)}</body></html>`;
    const blob = new Blob([doc], { type: "text/html" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "optimized-page.html";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="mx-auto max-w-6xl px-4 pb-24 pt-28 sm:px-6 lg:px-8">
        {/* Hero */}
        <div className="mb-8">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-brand">
            <Radar size={13} /> WebsiteTech Content Intelligence
          </div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
            SEO + <span className="text-brand">AI Visibility</span> Engine
          </h1>
          <p className="mt-3 max-w-2xl text-base text-slate-500">
            Analyze any page for traditional search <em>and</em> AI-powered discovery (AEO/GEO), get
            explainable fixes, entity insights and schema — then preview an AI-optimized rewrite. Nothing is published.
          </p>
        </div>

        {/* Input card */}
        <Card className="mb-8 border-slate-200 p-5">
          <div className="mb-4 flex gap-2">
            <button data-testid="mode-page-btn" onClick={() => setMode("page")}
              className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${mode === "page" ? "bg-brand text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              Analyze a page
            </button>
            <button data-testid="mode-site-btn" onClick={() => setMode("site")}
              className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${mode === "site" ? "bg-brand text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              Find my best page
            </button>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="seo-url-input"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runAnalyze()}
                placeholder={mode === "site" ? "yourdomain.com" : "https://yourdomain.com/page"}
                className="h-12 pl-10 text-base"
              />
            </div>
            <Button data-testid="seo-analyze-btn" onClick={() => runAnalyze()} disabled={loading} className="btn-primary h-12 gap-2 px-6 text-base">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Radar size={18} />}
              {loading ? "Analyzing…" : mode === "site" ? "Find & Analyze" : "Analyze"}
            </Button>
          </div>
          {error && (
            <div className="mt-3 flex items-center gap-2 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="seo-error">
              <AlertTriangle size={15} /> {error}
            </div>
          )}
        </Card>

        {/* Report */}
        {report && (
          <div className="space-y-6" data-testid="seo-report">
            {/* Summary */}
            <Card className="border-slate-200 p-5">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-slate-500">{report.final_url}</div>
                  <div className="truncate font-heading text-lg font-bold text-slate-900">{report.title || "Untitled page"}</div>
                </div>
                <Badge className={`border ${CLASS_STYLE[report.classification] || CLASS_STYLE.GOOD}`} data-testid="seo-classification">
                  {report.classification}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
                <ScoreTile label="Overall" value={report.scores.overall} />
                <ScoreTile label="SEO" value={report.scores.seo} />
                <ScoreTile label="AI Visibility" value={report.scores.ai_visibility} />
                <ScoreTile label="Content" value={report.scores.content} />
                <ScoreTile label="Technical" value={report.scores.technical} />
                <ScoreTile label="Mobile" value={report.scores.mobile} />
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                <Button data-testid="seo-rewrite-btn" onClick={runRewrite} disabled={rewriting || !report.content_readable} className="btn-primary gap-2">
                  {rewriting ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
                  {rewriting ? "Rewriting with AI…" : "Rewrite with AI"}
                </Button>
                <Button data-testid="seo-schema-btn" onClick={runSchema} disabled={schemaLoading} variant="outline" className="gap-2">
                  {schemaLoading ? <Loader2 size={16} className="animate-spin" /> : <Braces size={16} />}
                  Generate Schema
                </Button>
              </div>
            </Card>

            {/* Tabs */}
            <Tabs defaultValue="recommendations">
              <TabsList className="flex-wrap">
                <TabsTrigger value="recommendations" data-testid="tab-recommendations">
                  Recommendations {report.opportunity_count > 0 && <span className="ml-1.5 rounded-full bg-brand px-1.5 text-[11px] text-white">{report.opportunity_count}</span>}
                </TabsTrigger>
                <TabsTrigger value="aivis" data-testid="tab-aivis">AI Visibility</TabsTrigger>
                <TabsTrigger value="entities" data-testid="tab-entities">Entities</TabsTrigger>
              </TabsList>

              <TabsContent value="recommendations" className="mt-4 space-y-3">
                {report.recommendations.length === 0 && (
                  <Card className="border-emerald-200 bg-emerald-50 p-5 text-emerald-700">
                    <CheckCircle2 className="mb-1 inline" size={18} /> No critical issues found — this page is in good shape.
                  </Card>
                )}
                {report.recommendations.map((r, i) => (
                  <Card key={i} className="border-slate-200 p-4" data-testid={`rec-${i}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <Badge className={`border ${PRIO_STYLE[r.priority] || PRIO_STYLE.optional}`}>{r.priority}</Badge>
                        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{r.category}</span>
                      </div>
                    </div>
                    <div className="mt-2 font-semibold text-slate-900">{r.what}</div>
                    <div className="mt-1 text-sm text-slate-500"><span className="font-semibold text-slate-600">Why: </span>{r.why}</div>
                    {r.recommendation && <div className="mt-1 text-sm text-slate-600"><span className="font-semibold text-brand">Fix: </span>{r.recommendation}</div>}
                  </Card>
                ))}
              </TabsContent>

              <TabsContent value="aivis" className="mt-4 space-y-2">
                <Card className="border-slate-200 p-5">
                  <div className="mb-3 text-sm text-slate-500">How ready this page is to be understood and cited by AI answer engines:</div>
                  {report.ai_visibility_factors.map((f, i) => (
                    <div key={i} className="flex items-start gap-3 border-b border-slate-100 py-2.5 last:border-0" data-testid={`aivis-factor-${i}`}>
                      {f.ok ? <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-emerald-500" /> : <XCircle size={18} className="mt-0.5 shrink-0 text-slate-300" />}
                      <div>
                        <div className={`text-sm font-semibold ${f.ok ? "text-slate-900" : "text-slate-500"}`}>{f.factor} <span className="text-xs font-normal text-slate-400">(+{f.weight})</span></div>
                        <div className="text-xs text-slate-400">{f.detail}</div>
                      </div>
                    </div>
                  ))}
                </Card>
              </TabsContent>

              <TabsContent value="entities" className="mt-4">
                <Card className="border-slate-200 p-5">
                  <div className="mb-3 flex items-center gap-2 text-sm text-slate-500"><Tags size={15} /> Entities recognized on this page (knowledge-graph seed):</div>
                  {report.entities.length === 0 ? (
                    <div className="text-sm text-slate-400">No entities extracted.</div>
                  ) : (
                    <div className="flex flex-wrap gap-2" data-testid="entities-list">
                      {report.entities.map((e, i) => (
                        <span key={i} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm text-slate-700">
                          <span className="text-[10px] font-bold uppercase text-brand">{e.type}</span>{e.name}
                        </span>
                      ))}
                    </div>
                  )}
                </Card>
              </TabsContent>
            </Tabs>

            {/* Schema output */}
            {schema && (
              <Card className="border-slate-200 p-5" data-testid="schema-output">
                <div className="mb-2 flex items-center gap-2 font-heading font-bold text-slate-900"><Braces size={17} className="text-brand" /> Suggested JSON-LD Schema</div>
                <pre className="max-h-80 overflow-auto rounded-lg bg-slate-900 p-4 text-xs leading-relaxed text-emerald-300">
                  {JSON.stringify(schema, null, 2)}
                </pre>
              </Card>
            )}

            {/* Rewrite preview */}
            {rewrite && (
              <Card className="border-2 border-brand/30 p-5" data-testid="rewrite-output">
                <div className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-slate-900">
                  <Sparkles size={18} className="text-brand" /> AI-Optimized Rewrite (preview)
                </div>
                <div className="mb-4 rounded-lg bg-blue-50 p-3">
                  <div className="text-xs font-semibold uppercase tracking-wider text-brand">New title</div>
                  <div className="font-semibold text-slate-900">{rewrite.new_title}</div>
                  {rewrite.meta_description && <div className="mt-2 text-sm text-slate-600"><span className="font-semibold">Meta: </span>{rewrite.meta_description}</div>}
                </div>
                {rewrite.changes?.length > 0 && (
                  <div className="mb-4">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">What changed</div>
                    <ul className="space-y-1.5">
                      {rewrite.changes.map((c, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle2 size={15} className="mt-0.5 shrink-0 text-emerald-500" />{c}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Optimized article</div>
                <div className="prose prose-slate mt-2 max-w-none rounded-lg border border-slate-200 bg-white p-5"
                  dangerouslySetInnerHTML={{ __html: cleanHtml(rewrite.article_html) }} />
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  {isAdmin && (
                    <Button data-testid="seo-publish-btn" onClick={runPublish} disabled={publishing} className="btn-primary gap-2">
                      {publishing ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />}
                      {publishing ? "Publishing…" : "Publish live"}
                    </Button>
                  )}
                  <Button data-testid="seo-export-btn" onClick={exportHtml} variant="outline" className="gap-2">
                    <Download size={16} /> Export HTML
                  </Button>
                  {published && (
                    <a href={published.live_url} target="_blank" rel="noreferrer" data-testid="seo-live-link"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline">
                      <ExternalLink size={15} /> View live page (v{published.version_no})
                    </a>
                  )}
                </div>
                <div className="mt-3 text-xs text-slate-400">
                  {isAdmin
                    ? "Publish writes to a WebsiteTech-managed page at /p/… with full version history and one-click rollback. The original third-party URL is never modified — connect a CMS to auto-push there (coming soon)."
                    : "Preview only — the original URL and live content are unchanged. Publishing is an admin action."}
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Admin: connected sites */}
        {isAdmin && <AdminSites />}
        {isAdmin && <ManagedPages />}
      </main>
      <Footer />
    </div>
  );
}

function AdminSites() {
  const [sites, setSites] = useState([]);
  const [domain, setDomain] = useState("");
  const [name, setName] = useState("");
  const [langs, setLangs] = useState("en");
  const [busy, setBusy] = useState(false);
  const [crawling, setCrawling] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [job, setJob] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const pollJob = (jobId) => {
    setBulkBusy(true);
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const j = await seoJobStatus(jobId);
        setJob(j);
        if (j.status !== "completed" && j.status !== "cancelled" && tries < 120) {
          setTimeout(tick, 5000);
        } else {
          setBulkBusy(false);
        }
      } catch (e) { setBulkBusy(false); }
    };
    tick();
  };

  const bulkOptimize = async (id) => {
    setJob(null);
    try {
      const start = await seoBulkOptimize(id, 5);
      toast.success(`Queued ${start.total} pages for AI optimization`);
      pollJob(start.job_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start bulk optimize");
    }
  };

  const publishDraft = async (versionId) => {
    try { const r = await seoPublishDraft(versionId); toast.success(`Published live at ${r.live_url}`); }
    catch (e) { toast.error("Publish failed"); }
  };

  const load = async () => {
    try { const d = await seoListSites(); setSites(d.sites || []); } catch (e) { /* not admin / no data */ }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!domain.trim()) return;
    setBusy(true);
    try {
      const languages = langs.split(",").map((l) => l.trim().toLowerCase()).filter(Boolean);
      await seoCreateSite({ domain, name, languages: languages.length ? languages : ["en"] });
      setDomain(""); setName(""); setLangs("en");
      toast.success("Site connected");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add site"); }
    finally { setBusy(false); }
  };

  const crawl = async (id) => {
    setCrawling(id);
    try {
      const r = await seoCrawlSite(id);
      toast.success(`Crawled ${r.pages_analyzed} pages (SEO ${r.avg_seo}, AI ${r.avg_ai_visibility})`);
      load();
      if (selected === id) openDetail(id);
    } catch (e) { toast.error("Crawl failed"); }
    finally { setCrawling(null); }
  };

  const remove = async (id) => {
    try { await seoDeleteSite(id); toast.success("Site removed"); if (selected === id) { setSelected(null); setDetail(null); } load(); }
    catch (e) { toast.error("Could not remove"); }
  };

  const openDetail = async (id) => {
    setSelected(id); setJob(null);
    try {
      const [dash, pages, jobs] = await Promise.all([seoSiteDashboard(id), seoSitePages(id), seoJobsList(id).catch(() => ({ jobs: [] }))]);
      setDetail({ ...dash, pages: pages.pages });
      const active = (jobs.jobs || []).find((j) => j.status === "processing" || j.status === "queued");
      if (active) { setJob(active); pollJob(active.id); }  // re-attach to in-flight job
    } catch (e) { toast.error("Could not load site"); }
  };

  return (
    <div className="mt-14" data-testid="admin-sites">
      <div className="mb-4 flex items-center gap-2">
        <Globe size={18} className="text-brand" />
        <h2 className="font-heading text-lg font-bold text-slate-900">Connected Sites</h2>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">Admin</span>
      </div>

      <Card className="mb-5 border-slate-200 p-4">
        <div className="flex flex-col gap-3 sm:flex-row">
          <Input data-testid="site-domain-input" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="clientdomain.com" className="h-11 flex-1" />
          <Input data-testid="site-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Site name (optional)" className="h-11 flex-1" />
          <Input data-testid="site-langs-input" value={langs} onChange={(e) => setLangs(e.target.value)} placeholder="Languages e.g. en, ml" className="h-11 w-full sm:w-44" />
          <Button data-testid="site-add-btn" onClick={add} disabled={busy} className="btn-primary h-11 gap-2 px-5">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Connect
          </Button>
        </div>
      </Card>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {sites.map((s) => (
          <Card key={s.id} className="border-slate-200 p-4" data-testid={`site-card-${s.id}`}>
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="truncate font-semibold text-slate-900">{s.name}</div>
                <div className="truncate text-xs text-slate-400">{s.domain}</div>
              </div>
              <button onClick={() => remove(s.id)} className="text-slate-300 hover:text-rose-500" data-testid={`site-delete-${s.id}`}><Trash2 size={15} /></button>
            </div>
            <div className="mt-3 flex gap-4 text-sm">
              <div><span className="font-bold text-slate-900">{s.page_count}</span> <span className="text-slate-400">pages</span></div>
              <div><span className="font-bold" style={{ color: scoreTone(s.avg_seo).color }}>{s.avg_seo}</span> <span className="text-slate-400">SEO</span></div>
              <div><span className="font-bold" style={{ color: scoreTone(s.avg_ai_visibility).color }}>{s.avg_ai_visibility}</span> <span className="text-slate-400">AI</span></div>
            </div>
            <div className="mt-3 flex gap-2">
              <Button onClick={() => crawl(s.id)} disabled={crawling === s.id} size="sm" variant="outline" className="h-8 gap-1.5 text-xs" data-testid={`site-crawl-${s.id}`}>
                {crawling === s.id ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Crawl
              </Button>
              <Button onClick={() => openDetail(s.id)} size="sm" className="btn-primary h-8 gap-1.5 text-xs" data-testid={`site-open-${s.id}`}>
                <FileText size={13} /> Dashboard
              </Button>
            </div>
          </Card>
        ))}
        {sites.length === 0 && <div className="col-span-full text-sm text-slate-400">No sites connected yet. Add a domain above to build a content inventory.</div>}
      </div>

      {detail && (
        <Card className="mt-6 border-slate-200 p-5" data-testid="site-detail">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-heading font-bold text-slate-900">{detail.site.name} — Health {detail.health}/100</h3>
            <Button onClick={() => bulkOptimize(detail.site.id)} disabled={bulkBusy} size="sm" className="btn-primary h-9 gap-1.5" data-testid="bulk-optimize-btn">
              {bulkBusy ? <Loader2 size={15} className="animate-spin" /> : <Zap size={15} />}
              {bulkBusy ? "Optimizing…" : "AI Bulk Optimize (top 5)"}
            </Button>
          </div>

          {job && (
            <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50/60 p-4" data-testid="bulk-job">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-brand">
                <ListChecks size={16} /> Optimization queue — {job.status} ({job.done}/{job.total} done{job.failed ? `, ${job.failed} failed` : ""})
              </div>
              <div className="space-y-1.5">
                {job.items.map((it, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 text-sm" data-testid={`bulk-item-${i}`}>
                    <div className="flex min-w-0 items-center gap-2">
                      {it.status === "completed" ? <CheckCircle2 size={15} className="shrink-0 text-emerald-500" />
                        : it.status === "failed" ? <XCircle size={15} className="shrink-0 text-rose-500" />
                        : it.status === "processing" ? <Loader2 size={15} className="shrink-0 animate-spin text-brand" />
                        : <span className="h-3.5 w-3.5 shrink-0 rounded-full border border-slate-300" />}
                      <span className="truncate text-slate-600">{it.new_title || it.title || it.url}</span>
                    </div>
                    {it.status === "completed" && it.version_id && (
                      <button onClick={() => publishDraft(it.version_id)} className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-brand hover:underline" data-testid={`bulk-publish-${i}`}>
                        <Rocket size={12} /> Publish
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="mb-4 flex flex-wrap gap-2">
            {Object.entries(detail.by_class).filter(([, v]) => v > 0).map(([k, v]) => (
              <span key={k} className={`rounded-full border px-3 py-1 text-xs font-semibold ${CLASS_STYLE[k]}`}>{k}: {v}</span>
            ))}
          </div>
          {detail.top_opportunities?.length > 0 && (
            <div className="mb-4">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Top opportunity areas</div>
              <div className="flex flex-wrap gap-2">
                {detail.top_opportunities.map((o) => (
                  <span key={o.label} className="rounded-lg bg-blue-50 px-3 py-1 text-sm text-brand">{o.label} <span className="font-bold">{o.count}</span></span>
                ))}
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-400">
                  <th className="py-2 pr-3">Page</th><th className="px-2">Class</th><th className="px-2">SEO</th><th className="px-2">AI Vis</th><th className="px-2">Opps</th>
                </tr>
              </thead>
              <tbody>
                {detail.pages.length === 0 && (
                  <tr data-testid="inv-empty"><td colSpan={5} className="py-4 text-center text-sm text-slate-400">No pages yet — run a crawl to build the inventory.</td></tr>
                )}
                {detail.pages.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100" data-testid={`inv-row-${p.id}`}>
                    <td className="max-w-[280px] truncate py-2 pr-3 text-slate-600">{p.title || p.url}</td>
                    <td className="px-2"><span className={`rounded border px-1.5 py-0.5 text-[11px] font-semibold ${CLASS_STYLE[p.classification]}`}>{p.classification}</span></td>
                    <td className="px-2 font-semibold" style={{ color: scoreTone(p.scores.seo).color }}>{p.scores.seo}</td>
                    <td className="px-2 font-semibold" style={{ color: scoreTone(p.scores.ai_visibility).color }}>{p.scores.ai_visibility}</td>
                    <td className="px-2 text-slate-500">{p.opportunity_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <SeoPhase2 site={detail.site} />
        </Card>
      )}
    </div>
  );
}

// ─── SEO Phase 2: Competitor intelligence · hreflang · Cannibalization ───────
function SeoPhase2({ site }) {
  const [tab, setTab] = useState("competitors");
  const [compInput, setCompInput] = useState("");
  const [comp, setComp] = useState(null);
  const [compBusy, setCompBusy] = useState(false);
  const [hre, setHre] = useState(null);
  const [hreBusy, setHreBusy] = useState(false);
  const [can, setCan] = useState(null);
  const [canBusy, setCanBusy] = useState(false);

  useEffect(() => { seoGetCompetitors(site.id).then((r) => r?.comparison && setComp(r)).catch(() => {}); }, [site.id]);

  const runComp = async () => {
    const list = compInput.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 3);
    if (!list.length) { toast.error("Enter 1–3 competitor domains (comma-separated)."); return; }
    setCompBusy(true);
    try { const r = await seoRunCompetitors(site.id, list); setComp(r); toast.success("Competitor analysis ready"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Competitor analysis failed"); }
    finally { setCompBusy(false); }
  };
  const runHre = async () => {
    setHreBusy(true);
    try { setHre(await seoHreflang(site.id)); }
    catch (e) { toast.error(e?.response?.data?.detail || "hreflang audit failed"); }
    finally { setHreBusy(false); }
  };
  const runCan = async () => {
    setCanBusy(true);
    try { setCan(await seoCannibalization(site.id)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cannibalization check failed"); }
    finally { setCanBusy(false); }
  };

  const c = comp?.comparison;
  const List = ({ items, render }) => (
    <ul className="space-y-1.5">{(items || []).map((it, i) => <li key={i} className="text-sm text-slate-600">{render(it)}</li>)}</ul>
  );

  return (
    <div className="mt-8 border-t border-slate-200 pt-6" data-testid="seo-phase2">
      <div className="mb-4 flex items-center gap-2">
        <Sparkles size={16} className="text-brand" />
        <h4 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">Advanced Intelligence (Phase 2)</h4>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="competitors" data-testid="p2-tab-competitors"><Swords size={13} className="mr-1.5" /> Competitors</TabsTrigger>
          <TabsTrigger value="hreflang" data-testid="p2-tab-hreflang"><Languages size={13} className="mr-1.5" /> hreflang</TabsTrigger>
          <TabsTrigger value="cannibalization" data-testid="p2-tab-cannibalization"><Split size={13} className="mr-1.5" /> Cannibalization</TabsTrigger>
        </TabsList>

        {/* Competitors */}
        <TabsContent value="competitors" className="mt-4">
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input data-testid="p2-competitor-input" value={compInput} onChange={(e) => setCompInput(e.target.value)}
              placeholder="competitor1.com, competitor2.com (up to 3)" className="h-11 flex-1" />
            <Button data-testid="p2-competitor-run" onClick={runComp} disabled={compBusy} className="btn-primary h-11 gap-2 px-5">
              {compBusy ? <Loader2 size={16} className="animate-spin" /> : <Swords size={16} />} Analyze
            </Button>
          </div>
          {compBusy && <p className="mt-3 text-sm text-slate-400">Analyzing competitors with AI — this can take up to a minute…</p>}
          {c && (
            <div className="mt-4 space-y-4" data-testid="p2-competitor-report">
              {c.summary && <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-700">{c.summary}</p>}
              <div className="flex flex-wrap gap-2">
                {(comp.competitors || []).map((cp, i) => (
                  <span key={i} className="rounded-lg border border-slate-200 px-3 py-1 text-xs text-slate-600">
                    {cp.domain} · SEO <b style={{ color: scoreTone(cp.scores?.seo || 0).color }}>{cp.scores?.seo ?? "—"}</b> · AI <b style={{ color: scoreTone(cp.scores?.ai_visibility || 0).color }}>{cp.scores?.ai_visibility ?? "—"}</b>
                  </span>
                ))}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
                  <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-emerald-700"><Trophy size={14} /> Where you win</div>
                  <List items={c.you_win} render={(t) => `• ${t}`} />
                </div>
                <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-4">
                  <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-rose-700"><AlertTriangle size={14} /> Where they win</div>
                  <List items={c.they_win} render={(t) => `• ${t}`} />
                </div>
              </div>
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-600">Content gaps to close</div>
                <List items={c.content_gaps} render={(g) => <><b className="text-slate-800">{g.topic}</b> — {g.why}</>} />
              </div>
              {c.keyword_gaps?.length > 0 && (
                <div>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-600">Keyword gaps</div>
                  <div className="flex flex-wrap gap-2">{c.keyword_gaps.map((k, i) => <span key={i} className="rounded-lg bg-blue-50 px-3 py-1 text-sm text-brand">{k}</span>)}</div>
                </div>
              )}
              <div>
                <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-600"><Link2 size={13} /> Link-building opportunities <span className="normal-case text-slate-500">(AI-suggested, not a live backlink index)</span></div>
                <List items={c.link_opportunities} render={(o) => <><b className="text-slate-800">{o.tactic}</b> — {o.detail}</>} />
              </div>
            </div>
          )}
        </TabsContent>

        {/* hreflang */}
        <TabsContent value="hreflang" className="mt-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">Languages declared: {(site.languages || ["en"]).join(", ")}</p>
            <Button data-testid="p2-hreflang-run" onClick={runHre} disabled={hreBusy} size="sm" variant="outline" className="h-9 gap-1.5">
              {hreBusy ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Run audit
            </Button>
          </div>
          {hre && (
            <div className="mt-4 space-y-3" data-testid="p2-hreflang-report">
              <p className="text-sm text-slate-600">{hre.pages_needing_hreflang} of {hre.pages_checked} checked pages need hreflang fixes.</p>
              {(hre.results || []).map((r, i) => (
                <div key={i} className="rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm text-slate-700">{r.url}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${r.status === "ok" ? "bg-emerald-100 text-emerald-700" : r.status === "partial" ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>{r.status}</span>
                  </div>
                  {r.missing_languages?.length > 0 && <p className="mt-1 text-xs text-rose-500">Missing: {r.missing_languages.join(", ")}</p>}
                  <pre className="mt-2 overflow-x-auto rounded-lg bg-slate-900 p-3 text-[11px] text-slate-100">{(r.recommended_tags || []).join("\n")}</pre>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Cannibalization */}
        <TabsContent value="cannibalization" className="mt-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">Finds pages competing for the same keyword.</p>
            <Button data-testid="p2-cannibalization-run" onClick={runCan} disabled={canBusy} size="sm" variant="outline" className="h-9 gap-1.5">
              {canBusy ? <Loader2 size={15} className="animate-spin" /> : <Split size={15} />} Detect
            </Button>
          </div>
          {canBusy && <p className="mt-3 text-sm text-slate-400">Scanning your pages with AI…</p>}
          {can && (
            <div className="mt-4 space-y-3" data-testid="p2-cannibalization-report">
              {can.note && <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-700">{can.note}</p>}
              {can.conflicts?.length === 0 && !can.note && <p className="rounded-xl bg-emerald-50 p-4 text-sm text-emerald-700">No keyword cannibalization detected across {can.checked} pages. 🎉</p>}
              {(can.conflicts || []).map((cf, i) => (
                <div key={i} className="rounded-xl border border-amber-200 bg-amber-50/50 p-4" data-testid={`p2-conflict-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-slate-800">“{cf.keyword}”</span>
                    <span className="rounded-full bg-slate-900 px-2 py-0.5 text-xs font-semibold uppercase text-white">{cf.action}</span>
                  </div>
                  {cf.intent && <p className="mt-0.5 text-xs text-slate-500">Intent: {cf.intent}</p>}
                  <ul className="mt-2 space-y-0.5">{(cf.urls || []).map((u, j) => <li key={j} className="truncate text-xs text-slate-600">• {u}</li>)}</ul>
                  <p className="mt-2 text-sm text-slate-700">{cf.recommendation}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
